import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { tenantAPI } from '../services/api';
import { servicesOf } from '../config/services';
import { inEditor } from '../components/site-editor/SiteEdits';

// Resolves once per page load whether this request landed on a store's own
// subdomain (found: true, with its business_type) or the bare marketing
// domain / an unrecognized host (found: false -> show the landing page).
const TenantContext = createContext({ loading: true, found: false, businessType: 'restaurant' });

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState({ loading: true, found: false, businessType: 'restaurant' });
  // The website editor's unsaved changes (editor frame only), kept so they
  // still show if they arrive before the store has loaded.
  const previewRef = useRef({});

  useEffect(() => {
    tenantAPI.getCurrent()
      .then(({ data }) => {
        if (data.found) {
          setTenant({ loading: false, found: true, platformUrl: data.platform_url, slug: data.slug, id: data.id, settings: { ...(data.settings || {}), ...previewRef.current }, businessType: data.business_type, services: servicesOf(data.services, data.business_type), name: data.name, logoUrl: data.logo_url, description: data.description, subscriptionLocked: data.subscription_locked === true });
        } else {
          setTenant({ loading: false, found: false, platformUrl: data.platform_url, businessType: null });
        }
      })
      .catch(() => setTenant({ loading: false, found: false, businessType: null }));
  }, []);

  // Inside the website editor's preview frame, the editor sends its unsaved
  // changes (texts, hidden items, colors, template) to show them live.
  useEffect(() => {
    if (!inEditor()) return;
    const onMessage = e => {
      if (e.origin !== window.location.origin || e.source !== window.parent || e.data?.type !== 'se:preview') return;
      const preview = e.data.settings && typeof e.data.settings === 'object' ? e.data.settings : {};
      previewRef.current = preview;
      setTenant(t => ({ ...t, settings: { ...(t.settings || {}), ...preview } }));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return <TenantContext.Provider value={tenant}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  return useContext(TenantContext);
}
